import { StrictMode, Component, type PropsWithChildren, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { initializeApp } from 'firebase/app'
import { RouterProvider } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { I18nextProvider } from 'react-i18next'
import {
  AuthProvider,
  createPersistedQueryClient,
  initDatadog,
} from '@wehunt/core-web'
// The clubhouse domain owns the i18next singleton (initialised on import).
import { i18n } from '@wehunt/clubhouse-web-wehunt'
import { ToastProvider, consumeThemeFromUrl } from '@wehunt/ui'
import '@wehunt/ui/styles/tokens.css'
import '@wehunt/ui/styles/components.css'
import { appConfig } from './appConfig'
import { firebaseConfig } from './firebase-config'
import { router } from './router'
import { VersionBadge } from './VersionBadge'
import { AppleRepairNotice } from './AppleRepairNotice'
import { syncDocumentLanguage } from './documentLanguage'
import { consumeLanguageFromUrl } from './languageHandover'

// A `?theme=` on the URL is the web shop handing the member's light/dark choice
// over. index.html has normally adopted and stripped it before this bundle ran
// (the router parses the URL at import, so it must never see the parameter);
// this is the tested fallback and a no-op when the inline script did its job.
consumeThemeFromUrl()
// The same handover for the shop's language (`?lang=`).
consumeLanguageFromUrl(i18n)

initDatadog({
  applicationId: appConfig.datadog?.applicationId,
  clientToken: appConfig.datadog?.clientToken,
  service: appConfig.datadog?.service ?? 'wehunt-web',
  env: appConfig.datadog?.env,
  version: appConfig.datadog?.version,
})

initializeApp(firebaseConfig)

syncDocumentLanguage(i18n)

// Persist the queries that gate first-paint UX:
//   - 'clubhouse' covers the sidebar Hunts and Teams badges
//     (useHuntStats, useMyGroups) and Overview upcoming-hunts list.
//   - 'chronicle' covers the Overview top stats, season section,
//     activity panel, and team list.
// Detail-page queries (e.g. 'member' for a single user lookup) are
// intentionally excluded — fetching them fresh is fine.
const queryClient = createPersistedQueryClient({
  appKey: 'wehunt-web',
  // Bumped when the team-photos response grew the camera name the gallery
  // stamps over each picture. A restored entry keeps its original
  // dataUpdatedAt and these queries are gated on `enabled:` (the uid arrives
  // asynchronously), so nothing revalidates them on a reload — a returning
  // hunter would have gone on seeing unlabelled photos for up to the 24h
  // maxAge, which looks exactly like the bug still being there.
  //
  // Bumped again (3) when map pins grew their note (`description`). A cache
  // restored from before that held pins without notes, and the map editor
  // seeded from it; a Save then compared those against the refetched pins and
  // cleared every note on the team. The editor now diffs against its own seed,
  // but a cache in the old shape must still not be served as current.
  //
  // Any change to the SHAPE of a persisted clubhouse or chronicle query bumps
  // this value.
  buster: '3',
  persistedKeys: ['clubhouse', 'chronicle'],
})

// Rendered outside the I18nextProvider (the boundary wraps it), so it reads the
// instance directly. The English defaults stand in if translation is itself
// what failed.
function AppErrorFallback() {
  return (
    <div role="alert" style={{ padding: '2rem', textAlign: 'center' }}>
      <h1>{i18n.t('app:crash.title', { defaultValue: 'Something went wrong' })}</h1>
      <p>{i18n.t('app:crash.body', { defaultValue: 'The error has been reported. Please refresh the page or try again later.' })}</p>
    </div>
  )
}

class ErrorBoundary extends Component<
  PropsWithChildren<{ fallback: ReactNode }>,
  { hasError: boolean }
> {
  state = { hasError: false }
  static getDerivedStateFromError() {
    return { hasError: true }
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary fallback={<AppErrorFallback />}>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            {/* Above the router so a toast fired just before a navigation
                (e.g. "Invitations sent" → /hunts) survives the route change. */}
            <ToastProvider>
              <RouterProvider router={router} />
              {/* Alongside the router, not inside it: the Apple repair probe's
                  plan arrives while the user is already signed in, after
                  SignInPage has navigated away, so this has to render
                  regardless of the current route. */}
              <AppleRepairNotice />
            </ToastProvider>
          </AuthProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </ErrorBoundary>
    <VersionBadge />
  </StrictMode>
)
